import round from 'lodash/round'
import get from 'lodash/fp/get'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'

const METER_PER_MILE = 0.000621371192

const LOCALE_MAP = {
  THB: 'th-TH',
  CZK: 'cs-CZ',
  ZAR: 'af-ZA',
  SEK: 'en-SE',
}

export default {
  data() {
    return {
      availabilityTextMap: {
        'Check-in and Checkout today': 'Check-in/out',
        'Check-in today': 'Check-in',
        'Checkout today': 'Check-out',
        'Free today': 'Free',
        'Blocked today': 'Blocked',
      },
    }
  },
  computed: {
    currencySign() {
      const locale = get('config.locale', window) || 'US'
      const currency = new Intl.NumberFormat(`en-${locale}`, {
        style: 'currency',
        currency: get('config.currency', window) || 'USD',
        maximumFractionDigits: 0,
      })
      return currency.formatToParts(0).find(c => c.type === 'currency')?.value
    },
  },
  methods: {
    getCurrencySign(currency, locale) {
      const curr = currency || window.config?.currency || 'USD'
      const loc = locale || window.config?.locale || 'US'

      try {
        const formatter = new Intl.NumberFormat(`en-${loc}`, {
          style: 'currency',
          currency: curr,
          maximumFractionDigits: 0,
        })
        return (
          formatter.formatToParts(0).find(part => part.type === 'currency')
            ?.value || '$'
        )
      } catch (error) {
        return this.currencySign // Fallback
      }
    },
    toMoney(amount, config = {}) {
      if (!amount && amount !== 0) return null
      const fixedAmount = amount > -0.001 && amount < 0.001 ? 0 : amount
      const curr = config.currency || get('config.currency', window) || 'USD'
      const locale = LOCALE_MAP[curr] || 'en'
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: curr,
        notation: config.notation || 'standard',
        maximumFractionDigits: Number.isInteger(config.decimals)
          ? config.decimals
          : 2,
      }).format(fixedAmount)
    },
    /**
     * Converts amount to zone currency using reservation's zone_rate
     * @param {number} amount - The amount to convert
     * @param {object} reservation - Reservation object with zone_rate and listing.zone.currency
     * @param {object} config - Optional config (decimals, notation)
     * @returns {string} Formatted currency string in zone currency
     */
    toZoneMoney(amount, reservation, config = {}) {
      if (!amount && amount !== 0) return null
      const rate = reservation?.zone_rate || 1
      const zoneCurrency =
        reservation?.listing?.zone?.currency ||
        reservation?.currency_to ||
        reservation?.currency
      return this.toMoney(amount * rate, { ...config, currency: zoneCurrency })
    },
    parsePlaceAttribute(attribute, place) {
      const addressComponent =
        place.address_components.find(
          component => component.types[0] === attribute
        ) || {}
      if (isEmpty(addressComponent) || isNil(addressComponent.long_name)) {
        return ''
      }

      return addressComponent.long_name
    },
    parsePlaceShortAttribute(attribute, place) {
      const addressComponent =
        place.address_components.find(
          component => component.types[0] === attribute
        ) || {}
      if (isEmpty(addressComponent) || isNil(addressComponent.long_name)) {
        return ''
      }

      return addressComponent.short_name
    },
    toAmount(amount, maximumFractionDigits = 2) {
      if (!amount && amount !== 0) return null
      const fixedAmount = amount > -1 && amount < 1 ? 0 : amount
      return new Intl.NumberFormat(`en`, {
        maximumFractionDigits,
      }).format(fixedAmount)
    },
    sourceName(name = '') {
      if (name?.toLowerCase() === 'homeaway') {
        return 'VRBO'
      }
      return name
    },
    toPercent(num, decimal = 0) {
      return num && `${num ? (+num).toFixed(decimal) : '0'}%`
    },
    parseDate: function (date, format = 'YYYY-MM-DD', config = {}) {
      if (date) {
        const parsedDate = config.local
          ? this.$moment(date)
          : this.$moment.utc(date)
        if (parsedDate) {
          return parsedDate.format(format)
        }
      } else {
        return ''
      }
    },
    parseTimestamp: function (date, format = 'YYYY-MM-DD', config = {}) {
      if (date) {
        const parsedDate = config.local
          ? this.$moment.unix(date)
          : this.$moment.unix(date).utc()
        if (parsedDate) {
          return parsedDate.format(format)
        }
      } else {
        return ''
      }
    },
    fromNow(time) {
      return this.$moment(time).fromNow()
    },
    kFormatter(num, config = {}) {
      const { decimals = 2, currency } = config
      return Math.abs(num) > 999 && this.$vuetify.breakpoint.xs
        ? this.toMoney(Math.sign(num) * (Math.abs(num) / 1000), {
            decimals,
            currency,
          }) + 'k'
        : this.toMoney(Math.sign(num) * Math.abs(num), { decimals, currency })
    },
    metersToMiles(meters, decimals = 1) {
      return round(meters * METER_PER_MILE, decimals)
    },
    round(val, decimals = 0) {
      return val ? round(val, decimals) : val
    },
  },
}
